/**
 * Meu perfil (RN-013 foto / RN-005e nova senha / RN-014 currículo). Refeito em 04/09/2026 no
 * PADRÃO DESIGN, a pedido do Hilton: foto à esquerda (componente do paciente, funcionando de
 * verdade), dados do usuário e troca de senha ao centro, currículo na coluna mais larga.
 * A troca de usuário saiu: não existe endpoint e simular era pior que não ter.
 *
 * É formulário, não lista: do pacote se aplicam cabeçalho, painel único, caixas explicativas e
 * modais; filtros, tabela, contagem, paginação e poda de colunas não se aplicam pela natureza.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, Container, TextField, Typography } from "@mui/material";
import { LockReset } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ProfilePhoto, { type ProfilePhotoSource } from "../components/ProfilePhoto";
import CurriculumSection from "../components/CurriculumSection";
import PasswordInviteChannelModal, { formatWhatsApp } from "../components/modals/PasswordInviteChannelModal";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/RouterContext";
import { colors } from "../theme/designSystem";
import { usersService, type MyContact } from "../services/usersService";
import type { PasswordInviteOutcome } from "../services/entityUserService";

// Campo de formulário em COLUNA. Não usar os sx da barra de filtros aqui: eles carregam
// `flex: 1 1 <px>` para a linha, e numa coluna a base vira ALTURA.
const fieldSx = {
  backgroundColor: colors.white,
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: colors.border },
    "&:hover fieldset": { borderColor: colors.border },
    "&.Mui-focused fieldset": { borderColor: colors.primary },
  },
};

const readOnlyField = (label: string, value: string) => (
  <TextField
    size="small"
    label={label}
    value={value}
    fullWidth
    InputProps={{ readOnly: true }}
    InputLabelProps={{ shrink: true }}
    sx={fieldSx}
  />
);

const sectionTitleSx = { fontSize: "0.95rem", fontWeight: 600, color: colors.textPrimary, mb: 1.5 };

const UserProfile: React.FC = () => {
  const { goToDashboard } = useNavigation();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();

  const [contact, setContact] = useState<MyContact | null>(null);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [failedOutcome, setFailedOutcome] = useState<PasswordInviteOutcome | null>(null);

  useEffect(() => {
    document.title = "Clinic4Us - Meu perfil";
    usersService
      .getMyContact()
      .then(setContact)
      .catch(() =>
        setContact(
          user?.email
            ? { email: user.email, phoneDdi: null, phoneCode: null, phoneNumber: null, fullName: null, professionalTypeName: null, profileNames: [] }
            : null,
        ),
      );
  }, [user?.email]);

  const photoSource = useMemo<ProfilePhotoSource>(
    () => ({ load: () => usersService.getMyPhoto(), save: usersService.setMyPhoto, remove: usersService.removeMyPhoto }),
    [],
  );

  const onError = useCallback((m: string) => showToast(m, "error"), [showToast]);
  const onSuccess = useCallback((m: string) => showToast(m, "success"), [showToast]);

  const handleSendLink = useCallback(
    async (channels: { sendEmail: boolean; sendWhatsApp: boolean }) => {
      setSending(true);
      try {
        const outcome = await usersService.sendMyPasswordLink(channels);
        const saiu = [outcome.emailSent && "email", outcome.whatsappSent && "WhatsApp"].filter(Boolean);
        if (saiu.length > 0) {
          setLinkModalOpen(false);
          setFailedOutcome(null);
          showToast(`Link enviado por ${saiu.join(" e ")}. Ele vale por 2 horas.`, "success");
        } else {
          // Nenhum canal entregou (homolog, provedor fora): o modal fica aberto com o link.
          setFailedOutcome(outcome);
        }
      } catch (error) {
        showToast(
          (error as { response?: { data?: { description?: string } } })?.response?.data?.description || "Não foi possível gerar o link.",
          "error",
        );
      } finally {
        setSending(false);
      }
    },
    [showToast],
  );

  const displayName = contact?.fullName || user?.email?.split("@")[0] || "usuário";
  const whatsapp = formatWhatsApp(contact?.phoneDdi, contact?.phoneCode, contact?.phoneNumber);
  const profiles = contact?.profileNames?.length ? contact.profileNames : user?.profileName ? [user.profileName] : [];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={goToDashboard}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Meu perfil
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Sua foto, seus dados de acesso, a troca de senha e o seu currículo.
              </Typography>
            </Box>
            <FaqButton />
          </Box>

          <div className="c4u-panel">
            {/* Foto (largura do preview) | conta | currículo (a coluna que cresce). Empilha no celular. */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "260px minmax(0, 1fr) minmax(0, 1.4fr)" },
                gap: { xs: 3, md: 4 },
                alignItems: "start",
              }}
            >
              <Box>
                <Typography sx={sectionTitleSx}>Foto do perfil</Typography>
                <ProfilePhoto source={photoSource} />
              </Box>

              <Box>
                <Typography sx={sectionTitleSx}>Dados do usuário</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {readOnlyField("Nome", contact?.fullName ?? "não informado")}
                  {readOnlyField("Usuário (email)", contact?.email ?? user?.email ?? "")}
                  {readOnlyField("Celular", whatsapp ?? "não informado")}
                  {readOnlyField("Tipo de profissional", contact?.professionalTypeName ?? "não informado")}
                  {readOnlyField("Clínica", user?.clinicName ?? "")}
                </Box>

                <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textSecondary, mt: 2, mb: 0.75 }}>
                  Perfis de acesso nesta clínica
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {profiles.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: colors.textSecondary }}>—</Typography>
                  ) : (
                    profiles.map((p) => (
                      <Chip
                        key={p}
                        label={p}
                        size="small"
                        sx={{ fontWeight: 600, color: colors.primary, border: `1px solid ${colors.primary}`, backgroundColor: "transparent" }}
                      />
                    ))
                  )}
                </Box>
                <Typography sx={{ fontSize: 12, color: colors.textSecondary, mt: 1 }}>
                  Nome, email, celular, tipo e perfis são alterados pelo administrador da clínica, em Usuários.
                </Typography>

                <Typography sx={{ ...sectionTitleSx, mt: 3 }}>Nova senha</Typography>
                <Typography sx={{ fontSize: 13, color: colors.textSecondary, mb: 1.5 }}>
                  Você recebe um link para criar a nova senha — por email, WhatsApp ou os dois. Pessoal, de uso único, vale por 2 horas.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<LockReset />}
                  onClick={() => {
                    setFailedOutcome(null);
                    setLinkModalOpen(true);
                  }}
                  disabled={!contact}
                  sx={{
                    backgroundColor: colors.primary,
                    textTransform: "none",
                    minHeight: 44,
                    "&:hover": { backgroundColor: colors.primaryHover },
                    "&.Mui-disabled": { backgroundColor: colors.primary, opacity: 0.7, color: "white" },
                  }}
                >
                  Receber link para nova senha
                </Button>
              </Box>

              <CurriculumSection onError={onError} onSuccess={onSuccess} />
            </Box>

          </div>
        </Container>
      </Box>

      <PasswordInviteChannelModal
        key={linkModalOpen ? "open" : "closed"}
        open={linkModalOpen}
        userName={displayName}
        email={contact?.email ?? null}
        whatsapp={whatsapp}
        sending={sending}
        failedOutcome={failedOutcome}
        validityText="O link é pessoal, de uso único e vale por 2 horas. Um envio novo invalida o anterior."
        onClose={() => {
          setLinkModalOpen(false);
          setFailedOutcome(null);
        }}
        onSend={handleSendLink}
      />

      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default UserProfile;
